import { debounce } from "lodash-es"
import { observable, runInAction } from "mobx"

import { batchInfo } from "@/lib/apiClient/client.ts"
import { withGlobalLoader } from "@/lib/context.ts"
import { getLogger } from "@/lib/logging.ts"
import { Model } from "@/lib/Model.ts"
import { openLinkInNewWindow } from "@/lib/std.ts"

import type { PromptInputModel } from "@/components/PromptInput/PromptInputModel.ts"
import type { GitHubAccountInfo, GitHubRepo } from "@/lib/client/generated/TypeDefinitions.ts"
import type { OrgContext } from "@/lib/context.ts"
import type { IObservableValue } from "mobx"

const log = getLogger("NewProjectGitHubIntegrationModel")

export class NewProjectGitHubIntegrationModel extends Model {
  private cachedLinkGitHubAccountUrl: string | null = null

  readonly selectedAccountLogin = observable.box<string | null>(null)

  readonly searchQuery = observable.box<string>("")
  private debouncedSetSearchQuery: ((query: string) => void) | null = null

  readonly repositories: IObservableValue<GitHubRepo[]> = observable.box([])
  readonly hasMoreRepositories: IObservableValue<boolean> = observable.box(false)

  private nextOffset: string | null = null
  private readonly initialBatchSize = 3
  private readonly loadMoreBatchSize = 20

  readonly isLoadingRepositories: IObservableValue<boolean> = observable.box(false)

  constructor(
    private readonly context: OrgContext,
    private readonly inputModel: PromptInputModel
  ) {
    super("NewProjectGitHubIntegrationModel")
  }

  readonly handleImportFromGitHub = async () => {
    if (this.cachedLinkGitHubAccountUrl != null) {
      openLinkInNewWindow(this.cachedLinkGitHubAccountUrl)
      return
    }

    const url = await withGlobalLoader(this.context, (async () => {
      try {
        const oauthUrl = await this.context.queries.apiClient.startLinkGitHubAccountFlow()
        this.cachedLinkGitHubAccountUrl = oauthUrl
        return oauthUrl
      } catch {
        return null
      }
    })())

    if (url) {
      openLinkInNewWindow(url)
    }
  }

  readonly setSearchQueryDebounced = (query: string) => {
    if (!this.debouncedSetSearchQuery) {
      this.debouncedSetSearchQuery = debounce((q: string) => {
        runInAction(() => {
          this.searchQuery.set(q)
        })
      }, 400)
    }
    this.debouncedSetSearchQuery(query)
  }

  readonly setSelectedAccountLogin = (login: string | null) => {
    runInAction(() => {
      this.selectedAccountLogin.set(login)
    })
  }

  readonly handleRepositorySelect = async (repo: GitHubRepo) => {
    const githubUrl = `https://github.com/${repo.owner.login}/${repo.name}`
    this.inputModel.firstInputValue.set(githubUrl)
    await this.inputModel.doSubmit()
  }

  readonly loadRepositories = async (account: GitHubAccountInfo | undefined) => {
    const searchQuery = this.searchQuery.get()

    if (!account) {
      runInAction(() => {
        this.repositories.set([])
        this.hasMoreRepositories.set(false)
        this.nextOffset = null
      })
      return
    }

    // Reset state before loading
    this.resetRepositories()

    runInAction(() => {
      this.isLoadingRepositories.set(true)
    })

    try {
      const batch = await withGlobalLoader(
        this.context,
        this.context.queries.searchGitHubRepositories(
          account.login,
          account.accountType,
          searchQuery,
          batchInfo(this.initialBatchSize)
        )
      )

      runInAction(() => {
        this.repositories.set(batch.data)
        this.hasMoreRepositories.set(
          batch.totalCount !== null
            ? batch.totalCount > batch.data.length
            : batch.data.length === this.initialBatchSize
        )
        this.nextOffset = batch.next
        this.isLoadingRepositories.set(false)
      })
    } catch (e) {
      log.error("Failed to load GitHub repositories:", e)
      runInAction(() => {
        this.isLoadingRepositories.set(false)
        this.repositories.set([])
      })
    }
  }

  readonly loadMoreRepositories = async (account: GitHubAccountInfo | undefined) => {
    if (!this.nextOffset || this.isLoadingRepositories.get()) return

    const searchQuery = this.searchQuery.get()

    if (!account) return

    runInAction(() => {
      this.isLoadingRepositories.set(true)
    })

    try {
      const batch = await withGlobalLoader(
        this.context,
        this.context.queries.searchGitHubRepositories(
          account.login,
          account.accountType,
          searchQuery,
          batchInfo(this.loadMoreBatchSize, this.nextOffset)
        )
      )

      runInAction(() => {
        this.repositories.set([...this.repositories.get(), ...batch.data])
        this.hasMoreRepositories.set(
          batch.totalCount !== null
            ? batch.totalCount > batch.data.length
            : batch.data.length === this.loadMoreBatchSize
        )
        this.nextOffset = batch.next
        this.isLoadingRepositories.set(false)
      })
    } catch (e) {
      log.error("Failed to load more GitHub repositories:", e)
      runInAction(() => {
        this.isLoadingRepositories.set(false)
      })
    }
  }

  readonly resetRepositories = () => {
    runInAction(() => {
      this.repositories.set([])
      this.hasMoreRepositories.set(false)
      this.nextOffset = null
    })
  }
}
